import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FilterComponent } from "@client/shared/components";
import { globalStyles, PAGE_KEYS } from "@client/shared/constants";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {
  exportToFile,
  queryStringBuilder,
  getPageConfig,
  setPageConfig,
  getUserData,
} from "@client/utils";
import SiteView from "./view";
import Service from "./service";
import TextField from "@mui/material/TextField";
import { VALIDATIONS } from "@client/shared/constants";
import { validate as validator } from "@client/utils";

let gridHelper = null;
const defaultState = {
  entries: [],
  entriesCount: 0,
  isLoading: false,
  isExporting: false,
  pageNumber: 1,
  pageSize: 5,
  order: null,
  orderBy: "",
  filters: [],
  selectedRows: [],
  isFiltering: false,
  isSyncing: false,
  subscriptionId: null,
  isOpen: false,
  continueId: "",
  errors: {
    subscription_id: " ",
  },
  currentRow: {},
};

const Sites = () => {
  const pageConfig = getPageConfig(PAGE_KEYS.ADMIN_USER);
  const selectedUserId = sessionStorage.getItem("selectedUserId") || null;
  const [state, setState] = useState({
    ...defaultState,
    pageSize: pageConfig?.pageSize || defaultState.pageSize,
  });

  const userData = (getUserData() || {}).user || {};
  const hasDownloadAccess = userData?.roleAccess?.downloadData;

  const validateFields = (field, value) => {
    let errorMessage = "";
    const fieldValidatorMap = {
      subscription_id: [{ type: VALIDATIONS.MAX_LENGTH, value: 100 }],
    };

    if (fieldValidatorMap[field]) {
      const validationResult = fieldValidatorMap[field].map((validation) =>
        validator(
          value,
          validation.type,
          validation.value,
          validation.inputType || "string",
          validation.message
        )
      );
      errorMessage = validationResult
        .filter((error) => !error?.isValid)
        .map((error) => error?.errorMessage)[0];
    } else {
      Object.keys(fieldValidatorMap).forEach((key) => {
        const message = validateFields(key, state[key]);
        if (!!message) {
          errorMessage = message;
        }
      });
    }

    return errorMessage;
  };

  const handleChange = (evt, type = "input", row) => {
    const { name, value } =
      type === "input" ? evt.currentTarget || evt.target : evt;
    const errorMessage = validateFields(name, value) || " ";
    setState((prevState) => ({
      ...prevState,
      subscriptionId: value.trim(),
      currentRow: row,
      errors: {
        ...prevState.errors,
        [name]: errorMessage,
      },
    }));
  };
  const handleDialog = (id) => {
    setState((prevState) => ({
      ...prevState,
      isOpen: true,
      continueId: id,
    }));
  };

  const handlePatch = async (id, payload) => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      currentRow: defaultState.currentRow,
    }));

    const { error } = await Service.patch(id, payload);

    if (error) {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
      }));
      return toast.error(
        Array.isArray(error) ? error[0].message : error.message
      );
    }

    toast.success("Updated successfully.");
    setState((prevState) => ({
      ...prevState,
      isLoading: false,
      subscriptionId: "",
      continueId: "",
      isOpen: false,
    }));
    fetchEntries(
      state.pageNumber,
      state.pageSize,
      state.order,
      state.orderBy,
      state.filters
    );
  };
  const handleClose = () => {
    setState((prevState) => ({
      ...prevState,
      isOpen: false,
      currentRow: defaultState.currentRow,
    }));
  };
  const fetchEntries = useCallback(
    async (
      pageNumber = 1,
      pageSize = 5,
      order = "",
      orderBy = "",
      filters = []
    ) => {
      setState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
      const updatedFilters = filters.map((filter) => {
        const subFields = (
          filterConfig.find((item) => item.field === filter.field) || {}
        ).subFields;
        if (subFields) {
          filter.subFields = subFields;
        }
        return filter;
      });
      const queryString = queryStringBuilder(
        pageSize,
        (pageNumber - 1) * pageSize,
        [],
        updatedFilters,
        { field: orderBy, order }
      );
      const { data, error } = await Service.get(queryString);
      if (error) {
        setState((prevState) => ({
          ...prevState,
          isLoading: false,
        }));
        return toast.error(
          Array.isArray(error) ? error[0].message : error.message
        );
      }
      setState((prevState) => ({
        ...prevState,
        entries: data.sites || defaultState.entries,
        entriesCount: data?.count || defaultState.entriesCount,
        isLoading: false,
      }));
      return data || {};
    },
    []
  );

  const handlePageNumberChange = useCallback((pageNumber) => {
    setState((prevState) => ({
      ...prevState,
      pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handlePageSizeChange = useCallback((pageSize) => {
    setPageConfig(PAGE_KEYS.ADMIN_USER, {
      pageSize,
    });
    setState((prevState) => ({
      ...prevState,
      pageSize,
      pageNumber: defaultState.pageNumber,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleSortChange = useCallback((fieldObj, order) => {
    setState((prevState) => ({
      ...prevState,
      order,
      orderBy: order ? fieldObj.field || fieldObj.fieldName : null,
      selectedRows: defaultState.selectedRows,
    }));
    gridHelper && gridHelper.resetSelection();
  }, []);

  const handleFilterChange = useCallback((filters = []) => {
    setState((prevState) => ({
      ...prevState,
      filters,
      pageNumber: 1,
    }));
  }, []);

  const handleSelectionChange = useCallback((selectedRows) => {
    setState((prevState) => ({
      ...prevState,
      selectedRows,
    }));
  }, []);
  const filterConfig = [
    {
      field: "site_name",
      fieldToDisplay: "Site Name",
      operatorType: "string",
    },
    {
      field: "rc_site_id",
      fieldToDisplay: "Site ID",
      operatorType: "number",
    },
    {
      field: "subscription_id",
      fieldToDisplay: "Subscription ID",
      operatorType: "number",
    },
  ];
  const columnConfig = [
    {
      id: "rc_account_id",
      field: "rc_account_id",
      label: "Account UID",
      canSort: true,
      noResize: false,
    },
    {
      id: "site_name",
      field: "site_name",
      label: "Site Name",
      canSort: true,
      noResize: false,
    },
    {
      id: "rc_site_id",
      field: "rc_site_id",
      label: "Site ID",
      canSort: true,
      noResize: false,
    },
    {
      id: "subscription_id",
      field: "subscription_id",
      label: "Subscription ID",
      canSort: true,
      noResize: false,
      render: (row) => {
        return (
          <TextField
            name="subscription_id"
            placeholder="xxxxxx-xxxx-xxxx-xxxxx"
            size="small"
            fullWidth
            margin="dense"
            error={
              !!state.errors.subscription_id.trim() &&
              row.id === state.currentRow.id
            }
            helperText={
              row.id === state.currentRow.id
                ? state.errors.subscription_id
                : " "
            }
            defaultValue={row.subscription_id}
            value={
              row.id === state.currentRow.id
                ? state.subscriptionId
                : row.subscription_id
            }
            onChange={(evt) => {
              handleChange(evt, "input", row);
            }}
            onBlur={(evt) => {
              let value = evt?.target?.value;
              if(value === ""){
                value = null;
              }
              if (!!row.subscription_id && !value) {
                handleDialog(row.id);
              } else if (
                row.subscription_id !== value &&
                !state.errors.subscription_id.trim()
              ) {
                handlePatch(row.id, {
                  subscription_id: value,
                });
              } else {
                setState((prevState) => ({
                  ...prevState,
                  errors: prevState.errors,
                }));
              }
            }}
          />
        );
      },
    },
    {
      id: "col-action",
      label: "",
      renderHeader: () => (
        <Box
          sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.justify.end,
          }}
        >
          <Tooltip title="Filter Data Table" placement="top">
            <IconButton
              onClick={() => {
                setState((prevState) => ({
                  ...prevState,
                  isFiltering: true,
                }));
              }}
            >
              {state.filters.length !== 0 ? (
                <img src="/assets/filtered.svg" alt="Filter" />
              ) : (
                <img src="/assets/filter.svg" alt="Filter" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
      render: (row) => {
        <></>;
      },
    },
  ];

  useEffect(() => {
    fetchEntries(
      state.pageNumber,
      state.pageSize,
      state.order,
      state.orderBy,
      state.filters
    );
  }, [
    fetchEntries,
    state.pageNumber,
    state.pageSize,
    state.order,
    state.orderBy,
    state.filters,
  ]);

  return (
    <>
      <SiteView
        entries={state.entries}
        entriesCount={state.entriesCount}
        columnConfig={columnConfig}
        isLoading={state.isLoading}
        pageNumber={state.pageNumber}
        pageSize={state.pageSize}
        order={state.order}
        orderBy={state.orderBy}
        isOpen={state.isOpen}
        continueId={state.continueId}
        subscriptionId={state.subscriptionId}
        handlePageNumberChange={handlePageNumberChange}
        handlePageSizeChange={handlePageSizeChange}
        handleSortChange={handleSortChange}
        handleSelectionChange={handleSelectionChange}
        handleGridReady={(instance) => (gridHelper = instance)}
        handleClose={handleClose}
        handlePatch={handlePatch}
      />
      {state.isFiltering && (
        <FilterComponent
          isFiltering={state.isFiltering}
          filterConfig={filterConfig}
          onFilterApply={handleFilterChange}
          appliedFilters={state.filters}
          onFilterClose={() =>
            setState((prevState) => ({
              ...prevState,
              isFiltering: false,
            }))
          }
        />
      )}
    </>
  );
};

export default Sites;
